<template>
  <v-dialog
    v-model="isOpen"
    max-width="560"
    :persistent="persistent"
    scrollable
    overlay-opacity="0.15"
  >
    <v-card class="rounded-xl elevation-3 neo-surface">
      <v-toolbar dense flat class="mo-toolbar">
        <v-toolbar-title class="white--text d-flex align-center">
          <v-icon left color="white" class="mr-2">mdi-qrcode-scan</v-icon>
          {{ title }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeDialog" class="white--text">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-5 neo-surface">
        <div class="mb-3">
          <stream-barcode-reader
            v-if="isOpen"
            class="qr-stream"
            @decode="onScan"
            @loaded="onStreamLoaded"
          />
        </div>

        <v-text-field
          class="neo-input"
          v-model="manualCode"
          :label="inputLabel"
          placeholder="Arahkan scanner ke QR atau tempel kode"
          outlined
          clearable
          dense
          prepend-inner-icon="mdi-qrcode"
          @keyup.enter="submitManual"
        />

        <v-alert
          v-if="error"
          dense
          outlined
          :icon="'mdi-information-outline'"
          class="neo-alert mt-2"
        >
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4 neo-surface">
        <v-spacer />
        <v-btn class="neo-btn neo-pink elevation-0" @click="submitManual">{{
          actionLabel
        }}</v-btn>
        <v-btn text @click="closeDialog">Batal</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/*
  Lightweight QR wrapper using html5-qrcode if available on window.Html5Qrcode.
  Falls back to manual input when unavailable.
*/
import { StreamBarcodeReader } from 'vue-barcode-reader'
export default {
  name: 'ProductScanner',
  components: { StreamBarcodeReader },
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: 'Scan Reject Passet' },
    inputLabel: { type: String, default: 'Scan/Tempel QR Code' },
    actionLabel: { type: String, default: 'Reject' },
    autoCloseOnScan: { type: Boolean, default: false },
    autoEmitOnScan: { type: Boolean, default: true },
    persistent: { type: Boolean, default: true }
  },
  data() {
    return {
      isOpen: this.value,
      manualCode: '',
      error: ''
    }
  },
  computed: {},
  watch: {
    value(v) {
      this.isOpen = v
    },
    isOpen(v) {
      this.$emit('input', v)
    },
    manualCode(val) {
      if (val && this.error) this.error = ''
    }
  },
  mounted() {},
  beforeDestroy() {},
  methods: {
    onStreamLoaded() {
      this.error = ''
    },
    closeDialog() {
      this.manualCode = ''
      this.isOpen = false
    },
    onScan(text) {
      const code = (text || '').toString().trim()
      if (code) {
        this.manualCode = code
        if (this.autoEmitOnScan) {
          this.$emit('scanned', code)
          if (this.autoCloseOnScan) this.closeDialog()
        }
      }
    },
    submitManual() {
      const code = (this.manualCode || '').toString().trim()
      if (!code) {
        this.error = 'Silakan scan atau tempel QR code.'
        return
      }
      this.error = ''
      this.manualCode = ''
      this.$emit('scanned', code)
      if (this.autoCloseOnScan) this.closeDialog()
    }
  }
}
</script>

<style scoped>
.mo-toolbar {
  background: linear-gradient(135deg, #90caf9 0%, #a5d6a7 100%);
}

.neo-surface {
  /* Ensure background matches the button for neuromorphism */
  --bg: #f3f6fb;
  background: var(--bg);
  border-radius: 16px;
}
.neo-alert.v-alert {
  background: var(--bg) !important;
  border-color: rgba(233, 30, 99, 0.25) !important; /* soft pink border */
  color: #b0003a !important; /* darker pink text */
  border-radius: 12px !important;
  box-shadow: 6px 6px 12px rgba(0, 0, 0, 0.06),
    -6px -6px 12px rgba(255, 255, 255, 0.95) !important;
}
.neo-alert.v-alert .v-alert__content {
  font-size: 0.9rem;
}
.neo-alert.v-alert .v-icon {
  color: #d81b60 !important;
}
.neo-input /deep/ .v-input__slot,
.neo-input >>> .v-input__slot {
  background: var(--bg);
  border-radius: 9999px !important;
  border: none !important;
  /* tighter inset neuromorphic effect */
  box-shadow: inset 3px 3px 6px rgba(0, 0, 0, 0.1),
    inset -3px -3px 6px rgba(255, 255, 255, 0.95) !important;
  padding-left: 14px !important;
}
.neo-input /deep/ fieldset,
.neo-input >>> fieldset {
  border: 0 !important;
}
.neo-input /deep/ .v-text-field__details,
.neo-input >>> .v-text-field__details {
  display: none;
}
.neo-input /deep/ .v-input__prepend-inner .v-icon,
.neo-input >>> .v-input__prepend-inner .v-icon {
  color: #9aa4b2 !important;
}
.neo-input.v-input--is-focused /deep/ .v-input__slot,
.neo-input.v-input--is-focused >>> .v-input__slot {
  /* slightly stronger but still tight inset on focus */
  box-shadow: inset 4px 4px 13px rgba(0, 0, 0, 0.12),
    inset -4px -4px 8px rgba(255, 255, 255, 0.98) !important;
}
.qr-stream,
.qr-reader {
  width: 100%;
  min-height: 240px;
}

/* Neuromorphic button with soft pink shadows */
.neo-btn.v-btn {
  --bg: #f6f7fb;
  --hi: rgba(255, 255, 255, 0.95);
  --pink-shadow: rgba(233, 30, 99, 0.18); /* soft pink */
  background: var(--bg) !important;
  color: #d81b60 !important; /* pink text */
  border-radius: 14px !important;
  padding: 10px 18px !important;
  text-transform: none;
  box-shadow: 8px 8px 16px var(--pink-shadow), -8px -8px 16px var(--hi) !important;
  width: 80%;
}
.neo-btn.v-btn::before {
  /* remove Vuetify's overlay so shadows are visible */
  background-color: transparent !important;
}
.neo-btn.v-btn:hover {
  box-shadow: 10px 10px 20px var(--pink-shadow), -10px -10px 20px var(--hi) !important;
}
.neo-btn.v-btn:active {
  box-shadow: inset 8px 8px 18px var(--pink-shadow),
    inset -8px -8px 18px var(--hi) !important;
}
.neo-pink.v-btn {
  border: 1px solid rgba(255, 227, 237, 0.25) !important;
}
</style>
